// HighlightsCard — pure-presentation card for the Overview tab (PRODOPSAI-402).
//
// Renders the server-supplied IOverviewHighlight[] verbatim: a direction icon,
// a bold subject, magnitude prose, and an optional context line. The parent
// (OverviewTab, US4) owns the data fetch; this component only takes props in
// and emits JSX.
//
// Every visible string flows through `highlights` and `periodLabel` — the
// metric-definition registry composes the prose server-side so admin edits
// land without a frontend deploy. Defensive 6-row cap mirrors the server cap.

import type { IOverviewHighlight, OverviewHighlightKind } from '@skystone/shared';
import { Card, CardContent, CardHeader } from '@/components/ui';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Module-scope helpers — defined outside the component to satisfy SonarCloud
// rule S7721 (no helper functions/components inside the render).
// ---------------------------------------------------------------------------

type HighlightTone = 'success' | 'error' | 'warning';

interface IKindVisual {
  icon: string;
  tone: HighlightTone;
}

/**
 * Map each `OverviewHighlightKind` to its direction icon + semantic tone.
 *
 * Unicode triangles + warning sign mirror the source-spec mockup
 * (`&#9650; / &#9660; / &#9888;`); no SVG library is introduced.
 *
 * The 3-value union (PRODOPSAI-407) keeps the visual mapping flat: the LLM
 * analyst picks improvement / regression / ongoing_risk per item; subject
 * scope (single team / multi-team / org-wide) lives in the prose, not the kind.
 */
const KIND_VISUAL: Record<OverviewHighlightKind, IKindVisual> = {
  improvement: { icon: '▲', tone: 'success' },
  regression: { icon: '▼', tone: 'error' },
  ongoing_risk: { icon: '⚠', tone: 'warning' },
};

/**
 * Map a discriminated tone to a semantic Tailwind colour utility. Mirrors the
 * tokens used elsewhere in the FE (`text-success`, `text-error`,
 * `text-warning`) — no bespoke hex.
 */
const TONE_CLASS: Record<HighlightTone, string> = {
  success: 'text-success',
  error: 'text-error',
  warning: 'text-warning',
};

/** Server caps highlights at 6; the FE caps defensively at the same number. */
const MAX_HIGHLIGHTS = 6;

interface HighlightRowProps {
  highlight: IOverviewHighlight;
  isLast: boolean;
}

/**
 * One row in the highlights list. Direction icon on the left, text content on
 * the right (subject bold, magnitude plain weight, optional context italic
 * muted with leading em-dash). Bottom border on every row except the last.
 */
function HighlightRow({ highlight, isLast }: Readonly<HighlightRowProps>) {
  const visual = KIND_VISUAL[highlight.kind];
  return (
    <li
      data-testid="highlight-row"
      data-kind={highlight.kind}
      className={cn(
        'flex items-start gap-3 py-3',
        isLast ? '' : 'border-b border-border',
      )}
    >
      <span
        aria-hidden="true"
        data-testid="highlight-icon"
        className={cn('shrink-0 text-base leading-snug', TONE_CLASS[visual.tone])}
      >
        {visual.icon}
      </span>
      <div className="min-w-0 flex-1 text-sm leading-snug text-foreground">
        <p>
          <span className="font-bold">{highlight.subjectLabel}</span>
          <span className="ml-1 font-normal">{highlight.magnitudeLabel}</span>
          {highlight.provisional ? (
            <span
              data-testid="highlight-provisional"
              className="ml-2 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground"
            >
              so far this month
            </span>
          ) : null}
        </p>
        {highlight.contextLabel ? (
          <p className="mt-0.5 text-xs italic text-muted-foreground">
            {`— ${highlight.contextLabel}`}
          </p>
        ) : null}
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------

interface HighlightsCardProps {
  highlights: IOverviewHighlight[];
  periodLabel: string;
}

export function HighlightsCard({ highlights, periodLabel }: Readonly<HighlightsCardProps>) {
  const visibleHighlights = highlights.slice(0, MAX_HIGHLIGHTS);
  const isEmpty = visibleHighlights.length === 0;

  return (
    <Card data-testid="highlights-card">
      <CardHeader>
        <h3 className="text-xs font-bold uppercase tracking-wider text-foreground">
          {periodLabel}
        </h3>
      </CardHeader>
      <CardContent>
        {isEmpty ? (
          <p
            data-testid="highlights-empty"
            className="py-8 text-center text-sm text-muted-foreground"
          >
            No notable changes this period.
          </p>
        ) : (
          <ul data-testid="highlights-list" className="flex flex-col">
            {visibleHighlights.map((highlight, index) => (
              <HighlightRow
                key={`${highlight.metricKey}-${highlight.kind}-${index}`}
                highlight={highlight}
                isLast={index === visibleHighlights.length - 1}
              />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

export default HighlightsCard;
